import { alpha, createTheme, type ThemeOptions } from '@mui/material/styles';
import { color, cssEase, font, radius } from './design/tokens';

export const baseThemeOptions: ThemeOptions = {
    palette: {
        mode: 'dark',
        primary: { main: color.signal, contrastText: '#03121f' },
        secondary: { main: color.spark, contrastText: '#1d1203' },
        error: { main: color.danger },
        warning: { main: color.warning },
        success: { main: color.success },
        background: { default: color.void, paper: color.panelSolid },
        text: { primary: color.text, secondary: color.textMuted, disabled: color.textFaint },
        divider: color.line,
    },
    shape: { borderRadius: radius.sm },
    typography: {
        fontFamily: font.body,
        h1: { fontFamily: font.display, fontWeight: 500, letterSpacing: '-0.035em', lineHeight: 1.02 },
        h2: { fontFamily: font.display, fontWeight: 500, letterSpacing: '-0.03em', lineHeight: 1.05 },
        h3: { fontFamily: font.display, fontWeight: 500, letterSpacing: '-0.025em', lineHeight: 1.1 },
        h4: { fontFamily: font.display, fontWeight: 500, letterSpacing: '-0.02em' },
        h5: { fontFamily: font.display, fontWeight: 500, letterSpacing: '-0.015em' },
        h6: { fontFamily: font.display, fontWeight: 500, letterSpacing: '-0.01em' },
        overline: { fontFamily: font.mono, fontSize: '0.72rem', letterSpacing: '0.14em', lineHeight: 1.6 },
        button: { textTransform: 'none', fontFamily: font.display, fontWeight: 500, letterSpacing: '0.005em' },
    },
    transitions: {
        easing: {
            easeOut: cssEase.out,
            easeInOut: cssEase.snap,
            easeIn: cssEase.snap,
            sharp: cssEase.snap,
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: { backgroundColor: color.void },
            },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: {
                    borderRadius: radius.xs,
                    paddingInline: 18,
                    minHeight: 40,
                    transition: `background-color .3s ${cssEase.snap}, border-color .3s ${cssEase.snap}, color .3s ${cssEase.snap}, transform .2s ${cssEase.snap}`,
                    '&:active': { transform: 'scale(0.98)' },
                },
                containedPrimary: {
                    boxShadow: `0 0 0 1px ${alpha(color.signal, 0.5)}, 0 8px 32px -8px ${alpha(color.signal, 0.55)}`,
                    '&:hover': { backgroundColor: '#5cc2ff' },
                },
                outlined: {
                    borderColor: color.lineStrong,
                    color: color.text,
                    '&:hover': { borderColor: color.signal, backgroundColor: color.signalSoft },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: { root: { borderRadius: radius.xs } },
        },
        MuiPaper: {
            styleOverrides: {
                root: { backgroundImage: 'none', border: `1px solid ${color.line}` },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: 'rgba(14, 12, 38, 0.86)',
                    backdropFilter: 'blur(24px) saturate(140%)',
                    borderRadius: radius.md,
                },
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: { backgroundColor: 'rgba(3, 2, 10, 0.6)', backdropFilter: 'blur(4px)' },
                invisible: { backgroundColor: 'transparent', backdropFilter: 'none' },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: { backgroundColor: 'rgba(14, 12, 38, 0.92)', backdropFilter: 'blur(20px)' },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: 'rgba(14, 12, 38, 0.95)',
                    border: `1px solid ${color.line}`,
                    fontFamily: font.mono,
                    fontSize: '0.72rem',
                    letterSpacing: '0.04em',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: radius.xs,
                    backgroundColor: 'rgba(5, 4, 15, 0.5)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: color.line },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: color.lineStrong },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { borderRadius: radius.xs, fontFamily: font.mono, fontSize: '0.72rem', letterSpacing: '0.04em' },
            },
        },
        MuiTab: {
            styleOverrides: { root: { textTransform: 'none', fontFamily: font.display } },
        },
    },
};

const theme = createTheme(baseThemeOptions);

export default theme;
